import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeInterfaceHeader } from '@corefront/redux-aba';
import { useFeature } from '@growthbook/growthbook-react';
import { usePageTitle, useTranslate } from '../../../hooks/usePageTitle';
import { useABAPage } from '../../../hooks/useABAPage';
import {
  containerSelector,
  isLoggedInSelector,
  isNativeSelector,
} from '../../../redux/reducers/app/params';
import { getCookie } from '../../../utils/cookie';
import { readCSSVar } from '../../../utils/cssVarManipulation';
import { PageEvent, PageEventButton } from '../../../redux/helpers/aba-models';
import { queryClient } from '../../../services/queryClient';
import { frameMessageLogger, gameFrameEventCreator } from '../utils';
import {
  CASHIER_NAVIGATE_COOKIE_NAME,
  CASHIER_NAVIGATE_COOKIE_VALUE,
} from '../../../constants/ABA';
import { useIsSSCOMBrand } from '../../../hooks/useIsSSCOMBrand';
import FEATURES from '../../../constants/featureToggling';
import {
  CUSTOM_EVENT_IDS,
  IFRAME_EVENTS_DATA_TYPES,
} from '../../../constants/customEvents';
import { QUERY_KEY_GAME } from '../../../hooks/API/useQueryGames';

export const FRAME_HEADER_HEIGHT = 56;

export const useFrameSize = ({ isDemoMode }) => {
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  const container = useSelector(containerSelector);

  const resizeHandler = useCallback(() => {
    const DOM_NODE = document.querySelector(container);
    if (DOM_NODE) {
      DOM_NODE?.style?.setProperty?.(
        '--screen-height',
        `${window.innerHeight}px`
      );
      const height = readCSSVar(
        DOM_NODE,
        '--min-height-without-header-and-footer'
      );

      setSize({
        height: parseInt(height) - (isDemoMode ? FRAME_HEADER_HEIGHT : 0),
        width: window.innerWidth,
      });
    }
  }, [isDemoMode, container]);

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [resizeHandler]);

  return size;
};

export const backButton = new PageEventButton('back-click').toObject();

export const useDepositButton = () => {
  const translate = useTranslate();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const cashierNavigateValue = getCookie(CASHIER_NAVIGATE_COOKIE_NAME);
  const depositButton = useMemo(
    () =>
      new PageEventButton('redirect-cashier', translate('Deposit')).toObject(),
    [translate]
  );

  return useMemo(
    () =>
      cashierNavigateValue === CASHIER_NAVIGATE_COOKIE_VALUE && isLoggedIn
        ? depositButton
        : null,
    [cashierNavigateValue, depositButton, isLoggedIn]
  );
};

export const useGameNativeHeader = (isDemoGame = false) => {
  const title = usePageTitle();
  const dispatch = useDispatch();
  const isNative = useSelector(isNativeSelector);
  const isSCCOM = useIsSSCOMBrand();
  const depositButton = useDepositButton();

  const pageEvent = useMemo(() => {
    const primary = isDemoGame ? null : depositButton;
    let secondary = backButton;

    if (isSCCOM) {
      secondary = isDemoGame ? null : backButton;
    }

    return new PageEvent('gameId', title, primary, secondary).toObject();
  }, [title, isDemoGame, isSCCOM, depositButton]);

  useABAPage(pageEvent, isNative || isSCCOM);

  useEffect(() => {
    if (isNative) {
      dispatch(changeInterfaceHeader(true));
    }
  }, [dispatch, isNative, pageEvent]);
};

export const useChangeUserBalanceWatcher = (
  game = {},
  isIframeModeEnabled = false
) => {
  const isFeatureEnabled = useFeature(FEATURES.ENABLE_BALANCE_REFRESH).on;
  const frameRef = useRef(null);

  const { smartRefreshBalance } = game;

  const container = useSelector(containerSelector);
  const handler = useCallback(
    (e) => {
      if (smartRefreshBalance) {
        frameMessageLogger.log(`Game supports smartRefreshBalance`);
        frameMessageLogger.log('frame: ', frameRef?.current);
        frameMessageLogger.log(
          'dispatched event: ',
          gameFrameEventCreator(IFRAME_EVENTS_DATA_TYPES.ChangeBalance, e)
        );
        frameRef?.current?.contentWindow?.postMessage(
          gameFrameEventCreator(IFRAME_EVENTS_DATA_TYPES.ChangeBalance, e),
          '*'
        );
      } else {
        frameMessageLogger.log(`Game doesn't support smartRefreshBalance`);
        // resetting query for getting a possible new link
        // for game start
        queryClient.resetQueries([QUERY_KEY_GAME]);
      }
    },
    [smartRefreshBalance]
  );

  useEffect(() => {
    const DOM_NODE = document.querySelector(container);
    if (isFeatureEnabled && isIframeModeEnabled && DOM_NODE) {
      frameMessageLogger.log(
        `added event listener for ${CUSTOM_EVENT_IDS.ChangeUserBalance} event`
      );
      DOM_NODE?.addEventListener?.(CUSTOM_EVENT_IDS.ChangeUserBalance, handler);
    }
    return () => {
      frameMessageLogger.log(
        `removed event listener for ${CUSTOM_EVENT_IDS.ChangeUserBalance} event`
      );
      DOM_NODE?.removeEventListener?.(
        CUSTOM_EVENT_IDS.ChangeUserBalance,
        handler
      );
    };
  }, [isFeatureEnabled, handler, isIframeModeEnabled, container]);

  return {
    frameRef,
  };
};
